<template>
  <div class="personal-data">
    <div>
      <h4 class="form-section-title">
        {{ $t('user-manager.editing.personal-data.general-data') }}
      </h4>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-group
            class="personal-data__form-field"
            :label="$t('user-manager.editing.personal-data.name') + ' *'"
            label-for="name"
          >
            <b-form-input
              v-model.trim="user.name"
              id="name"
              maxlength="100"
              :state="$bsStateValidator($v.user.name.$error)"
              @blur.native="$v.user.name.$touch()"
            ></b-form-input>
            <b-form-invalid-feedback
              v-if="showNameSurnameTooltip($v.user.name)"
              :state="$bsStateValidator($v.user.name.$error)">
              {{
                $t('messages.MG001.message', {
                  0: 2,
                  1: 100,
                })
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-group
            label-for="surname"
            :label="$t('user-manager.editing.personal-data.lastname') + ' *'"
          >
            <b-form-input
              v-model.trim="user.surname"
              id="surname"
              maxlength="200"
              :state="$bsStateValidator($v.user.surname.$error)"
              @blur.native="$v.user.surname.$touch()"
            ></b-form-input>
            <b-form-invalid-feedback
              v-if="showNameSurnameTooltip($v.user.surname)"
              :state="$bsStateValidator($v.user.surname.$error)">
              {{
                $t('messages.MG001.message', {
                  0: 2,
                  1: 200,
                })
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-group
            label-for="language"
            :label="$t('user-manager.editing.personal-data.language') + ' *'"
          >
            <language-select
              v-model="user.language"
              id="language"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <label for="timezone">
            {{ $t('my-data.timezone') }}
            <info-badge
              class="personal-data__field-tooltip"
              translation-key="messages.MI086.message"
            />
          </label>
          <timezone-select
            id="timezone"
            v-model="user.timezoneId"
          />
        </b-col>
      </b-row>
    </div>
    <div class="mt-3">
      <h4 class="form-section-title">
        {{ $t('user-manager.editing.personal-data.contact') }}
      </h4>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-group
            class="personal-data__form-field"
            label-for="email"
            :label="$t('user-manager.editing.personal-data.email') + ' *'"
          >
            <b-form-input
              v-model="user.email"
              v-s-trigger="onEmailLostFocus"
              id="email"
              type="email"
              maxlength="250"
              :state="$bsStateValidator($v.user.email.$error)"
              @input="emailChanged"
            ></b-form-input>
            <b-form-invalid-feedback
              v-if="showEmailValidationError"
              :state="$bsStateValidator($v.user.email.$error)">
              {{ emailErrors }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-group
            label-for="country-code"
            :label="$t('user-manager.editing.personal-data.country-ddi') + ' *'"
          >
            <country-ddi-select
              v-model="user.countryCode"
              id="country-code"
            />
          </b-form-group>
        </b-col>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-group
            label-for="phone"
            :label="dddPhoneLabel"
          >
            <phone-input
              v-model="user.phone"
              id="phone"
              :state="$bsStateValidator($v.user.phone.$error)"
              @blur.native="$v.user.phone.$touch()"
            />
            <b-form-invalid-feedback
              v-if="$v.user.phone.$error"
              :state="$bsStateValidator($v.user.phone.$error)">
              {{ $t('messages.MA111.message') }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <div>
      <h4 class="form-section-title">
        {{ $t('user-manager.editing.personal-data.notifications') }}
      </h4>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-checkbox
            class="personal-data__form-field"
            v-model="user.receiveEmailAlerts"
            :disabled="!canChanceReceiveEmailAlerts"
          >
            {{ $t('user-manager.editing.personal-data.send-alerts-by-email') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-checkbox
            class="personal-data__form-field-last-notif-check"
            v-model="user.receiveSmsAlerts"
            :disabled="!canChanceReceiveSmsAlerts"
          >
            {{ $t('user-manager.editing.personal-data.send-alerts-by-sms') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
    </div>
    <div>
      <h4 class="form-section-title">
        {{ $t('user-manager.editing.personal-data.access-login') }}
      </h4>
      <b-row>
        <b-col
          sm
          md="6"
          lg="4"
        >
          <b-form-group
            class="personal-data__form-field"
            label-for="username"
            :label="$t('user-manager.editing.personal-data.username') + ' *'"
          >
            <b-form-input
              class="text-lowercase"
              v-model.lazy="$v.user.username.$model"
              v-s-trigger="onUserNameLostFocus"
              @blur.native="$v.user.username.$touch()"
              id="username"
              maxlength="20"
              :state="$bsStateValidator($v.user.username.$error)"
              :disabled="!isInserting"
              @input="usernameChanged"
            ></b-form-input>
            <b-form-invalid-feedback
              v-if="showUsernameValidationError"
              :state="$bsStateValidator($v.user.username.$error)">
              {{
                userNameErrors
              }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <b-alert
      class="personal-data__info-alert"
      show
      variant="info"
    >
      <h4 class="personal-data__info-title alert-heading">
        <strong>{{ $t('user-manager.editing.personal-data.temporary-password') }}</strong>
      </h4>
      <p>{{ $t('user-manager.editing.personal-data.auto-pass') }}</p>
    </b-alert>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  required, maxLength, minLength, email,
} from 'vuelidate/lib/validators';
import CountryDdiSelect from '@/domain/components/user/CountryDdiSelect';
import LanguageSelect from '@/domain/components/user/LanguageSelect';
import PhoneInput from '@/domain/components/user/PhoneInput';
import UserService from '@/domain/services/user';
import TimezoneSelect from '@/domain/components/user/TimezoneSelect';
import InfoBadge from '@/components/icon/InfoBadge';

const userNameValidChars = /^[\w.]*$/;
const invalidCharacters = /[^a-zA-Z0-9._]+/;

export function uniqueEmailValidator() {
  return !this.uniqueEmailValidationLoading && this.uniqueEmail;
}

export function uniqueUserNameValidator() {
  return !this.uniqueUserNameValidationLoading && this.uniqueUserName;
}

export default {
  components: {
    PhoneInput,
    CountryDdiSelect,
    LanguageSelect,
    TimezoneSelect,
    InfoBadge,
  },
  props: {
    user: Object,
    currentUserEmail: String,
  },
  data() {
    return {
      showEmailErrors: false,
      uniqueEmail: true,
      uniqueEmailAlreadyVerified: true,
      uniqueEmailValidationLoading: false,
      showUserNameErrors: false,
      uniqueUserName: true,
      uniqueUsernameAlreadyVerified: true,
      uniqueUserNameValidationLoading: false,
    };
  },
  validations() {
    const validChars = value => userNameValidChars.test(value);
    return {
      user: {
        name: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(100),
        },
        surname: {
          required,
          minLength: minLength(2),
          maxLength: maxLength(200),
        },
        email: {
          required,
          email,
          maxLength: maxLength(250),
          mustBeUnique: uniqueEmailValidator,
        },
        countryCode: {
          required,
        },
        username: {
          validChars,
          minLength: minLength(2),
          maxLength: maxLength(20),
          required,
          mustBeUnique: uniqueUserNameValidator,
        },
        phone: {
          required: () => !this.user.receiveSmsAlerts || !!this.user.phone,
          minLength: minLength(14),
          maxLength: maxLength(15),
        },
      },
    };
  },
  methods: {
    onEmailLostFocus() {
      this.validateEmail();
      this.showEmailErrors = true;
      this.$v.user.email.$touch();
    },
    emailChanged() {
      this.uniqueEmailAlreadyVerified = false;
    },
    usernameChanged(newUserName) {
      this.uniqueUsernameAlreadyVerified = false;
      if (newUserName) {
        setTimeout(() => {
          this.$set(this.user, 'username', newUserName.replace(invalidCharacters, ''));
        }, 0);
      }
      this.showUserNameErrors = true;
    },
    onUserNameLostFocus() {
      this.validateUsername();
      this.showUserNameErrors = true;
      this.showUsernameValidationError = true;
    },
    validateEmail() {
      if (!this.user.email) {
        this.uniqueEmail = true;
        return;
      }

      if (this.user.email.toLowerCase() === (this.currentUserEmail || '').toLowerCase()) {
        this.uniqueEmailAlreadyVerified = true;
        return;
      }

      this.uniqueEmailValidationLoading = true;
      UserService
        .isEmailAvailable(this.user.email)
        .then((available) => {
          this.uniqueEmail = Boolean(available);
          this.uniqueEmailValidationLoading = false;
          this.uniqueEmailAlreadyVerified = true;
        });
    },
    validateUsername() {
      if (!this.user.username) {
        this.uniqueUserName = true;
        return;
      }
      this.uniqueUserNameValidationLoading = true;
      UserService
        .isUsernameAvailable(this.user.username)
        .then((available) => {
          this.uniqueUserName = Boolean(available);
          this.uniqueUserNameValidationLoading = false;
          this.uniqueUsernameAlreadyVerified = true;
        });
    },
    showNameSurnameTooltip(validationModel) {
      return validationModel.$error
        && (!validationModel.required
          || !validationModel.minLength
        );
    },
  },
  computed: {
    ...mapGetters({
      loggedUser: 'user',
      isSuperAdmin: 'isSuperAdmin',
    }),
    canChanceReceiveEmailAlerts() {
      return this.isSuperAdmin || this.loggedUser.receiveEmailAlerts;
    },
    canChanceReceiveSmsAlerts() {
      return this.isSuperAdmin || this.loggedUser.receiveSmsAlerts;
    },
    isInserting() {
      return this.user.id == null;
    },
    showUsernameValidationError() {
      const isLostFocus = this.showUserNameErrors
        && (!this.user.username && !this.$v.user.username.minLength);

      const isInvalidFill = this.showUserNameErrors
        || !this.$v.user.username.minLength || !this.uniqueUserName;

      return !isLostFocus || !isInvalidFill;
    },
    showEmailValidationError() {
      return (!this.$v.user.email.required || !this.$v.user.email.email || !this.uniqueEmail);
    },
    dddPhoneLabel() {
      let label = this.$t('user-manager.editing.personal-data.ddd-phone');

      if (this.user.receiveSmsAlerts) {
        label += ' *';
      }

      return label;
    },
    disableNextButton() {
      return this.$v.$invalid
        || !this.uniqueEmailAlreadyVerified
        || !this.uniqueUsernameAlreadyVerified;
    },
    canForward() {
      return !this.disableNextButton;
    },
    emailErrors() {
      if (this.user.email && !this.uniqueEmail) {
        return this.$t('messages.MA012.message');
      }

      return this.$t('messages.MG002.message');
    },
    userNameErrors() {
      if (!this.$v.user.username.minLength || !this.user.username) {
        return this.$t('messages.MG001.message', { 0: '2', 1: '20' });
      }

      if (!this.uniqueUserName) {
        return this.$t('messages.MG003.message');
      }

      return null;
    },
  },
  watch: {
    canForward(newValue) {
      this.$emit('can-forward-changed', newValue);
    },
  },
};
</script>

<style lang="sass" scoped>
.personal-data
  &__field-tooltip
      ::v-deep .info-badge__icon
        color: var(--font-color-primary)

  &__info-title
    font-size: 0.9em
    font-weight: bold
    margin: 0 0 5px 0

  &__form-field
    margin: 5px 0 15px 0

    &-last-notif-check
      margin: 5px 0 1rem

  &__info-alert
    border-color: var(--color-info)

    p
      margin-bottom: 0
</style>
